import type { CSSProperties } from "react";
import { DENOMS, type DenomCounts } from "../../lib/denominations";

interface Props {
  value: DenomCounts;
  onChange: (next: DenomCounts) => void;
  /** Single-column rows and a 2-wide roll bar: phones and narrow cards. */
  compact: boolean;
}

// Same format the page's fmtCents used for these subtotals before the extraction.
const fmt = (cents: number) => "$" + (cents / 100).toFixed(2);

/** Per-denomination steppers plus coin-roll shortcuts. The caller renders the total. */
export default function DenominationCounter({ value, onChange, compact }: Props) {
  const bump = (cents: number, delta: number) => {
    const key = String(cents);
    onChange({ ...value, [key]: Math.max(0, (value[key] || 0) + delta) });
  };

  return (
    <>
      <div style={compact ? s.denomGridMobile : s.denomGrid}>
        {DENOMS.map((d) => {
          const qty = value[String(d.cents)] || 0;
          return (
            <div key={d.cents} style={compact ? s.denomCardMobile : s.denomItem}>
              <div style={s.denomLeftInfo}>
                <span style={s.denomLabelBadge}>{d.label}</span>
                <span style={s.denomSubtotal}>{fmt(qty * d.cents)}</span>
              </div>

              <div style={s.stepperWrap}>
                <button
                  type="button"
                  onClick={() => bump(d.cents, -1)}
                  style={s.stepperBtn}
                  title="Minus 1"
                  aria-label={`Decrease ${d.label}`}
                >
                  −
                </button>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min="0"
                  style={compact ? s.denomInputMobile : s.denomInput}
                  value={qty || ""}
                  placeholder="0"
                  aria-label={`${d.label} count`}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10) || 0;
                    onChange({ ...value, [String(d.cents)]: Math.max(0, val) });
                  }}
                />
                <button
                  type="button"
                  onClick={() => bump(d.cents, 1)}
                  style={s.stepperBtn}
                  title="Plus 1"
                  aria-label={`Increase ${d.label}`}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Coin Rolls Helpers */}
      <div style={compact ? s.coinRollsBarMobile : s.coinRollsBar}>
        <span style={s.coinRollHeading}>Quick Rolls:</span>
        <button type="button" onClick={() => bump(25, 40)} style={s.coinRollPill}>
          +$10 Qtrs (40)
        </button>
        <button type="button" onClick={() => bump(10, 50)} style={s.coinRollPill}>
          +$5 Dimes (50)
        </button>
        <button type="button" onClick={() => bump(5, 40)} style={s.coinRollPill}>
          +$2 Nickels (40)
        </button>
        <button type="button" onClick={() => bump(1, 50)} style={s.coinRollPill}>
          +50¢ Pennies (50)
        </button>
      </div>
    </>
  );
}

const s: Record<string, CSSProperties> = {
  denomGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 8,
    width: "100%",
  },
  denomGridMobile: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
  },
  denomItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "rgba(0,0,0,0.2)",
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.03)",
  },
  denomCardMobile: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border, #2A1F14)",
    padding: "8px 12px",
    borderRadius: 8,
    width: "100%",
    boxSizing: "border-box",
  },
  denomLeftInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  denomLabelBadge: {
    fontSize: 14,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    padding: "4px 8px",
    borderRadius: 6,
    letterSpacing: 0.5,
    minWidth: 44,
    textAlign: "center",
  },
  denomSubtotal: {
    fontSize: 14,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    whiteSpace: "nowrap",
  },
  stepperWrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: "0 0 auto",
  },
  stepperBtn: {
    width: 44,
    height: 42,
    background: "rgba(249, 175, 54, 0.14)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    borderRadius: 8,
    color: "var(--gold, #F9AF36)",
    fontSize: 22,
    fontWeight: 800,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
  },
  denomInput: {
    width: 64,
    height: 42,
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "4px 6px",
    fontSize: 16,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
    textAlign: "center",
  },
  denomInputMobile: {
    width: 58,
    height: 42,
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "2px 4px",
    fontSize: 18,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
    textAlign: "center",
    boxSizing: "border-box",
  },
  // Equal-width grid so every roll button is the same size and the same
  // distance apart — staff should never have to guess which pill they hit.
  coinRollsBar: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
    marginTop: 12,
    background: "rgba(255,255,255,0.02)",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },
  coinRollsBarMobile: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 12,
    background: "rgba(255,255,255,0.02)",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },
  coinRollHeading: {
    gridColumn: "1 / -1",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--muted, #8A7A66)",
  },
  coinRollPill: {
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    borderRadius: 8,
    color: "var(--gold, #F9AF36)",
    fontSize: 14,
    lineHeight: 1.2,
    padding: "12px 10px",
    cursor: "pointer",
    fontWeight: 700,
    minHeight: 48,
    width: "100%",
    textAlign: "center",
    // Wraps ("+$2 Nickels" / "(40)") rather than run past its cell: the 4-column bar gives each
    // pill 122px at 768–1024, and "+$2 Nickels (40)" needs 140px on one line.
    whiteSpace: "normal",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },
};
